import WarningIcon from '@/assets/warning.svg';
import { Button } from '@/components/ui/button';
import type { MediaErrorInfo } from '@/hooks/get-user-media-error-info';

interface MediaErrorOverlayProps {
  /**
   * The structured error to display. When null, the overlay renders nothing.
   */
  error: MediaErrorInfo | null;
  /**
   * Invoked when the user clicks the retry button. Only rendered for
   * errors with `recoverable: true`.
   */
  onRetry?: () => void;
  /**
   * Optional extra Tailwind classes applied to the outer wrapper.
   */
  className?: string;
}

/**
 * Red error card used wherever a getUserMedia failure needs to be shown
 * to the user. Presents the error title, a one-sentence explanation,
 * a bulleted list of things the user can try, and an optional retry
 * button. Rendered in the device-selection panel below the video tiles.
 */
export default function MediaErrorOverlay({
  error,
  onRetry,
  className,
}: MediaErrorOverlayProps) {
  if (!error) return null;

  const wrapperClass = [
    'rounded-lg border border-red-200 bg-red-50 p-4 text-left',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClass} role="alert">
      <div className="mb-2 flex items-center gap-2">
        <WarningIcon className="h-4 w-4 text-red-600" />
        <span className="text-sm font-medium text-red-800">{error.title}</span>
      </div>
      <p className="mb-3 text-sm text-red-700">{error.message}</p>
      {error.advice.length > 0 && (
        <ul className="mb-3 list-inside list-disc space-y-1 text-xs text-red-700">
          {error.advice.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
      {error.recoverable && onRetry && (
        <Button
          className="bg-red-100 px-3 py-1 text-sm text-red-800 hover:bg-red-200"
          onClick={onRetry}
          variant="secondary"
        >
          Retry camera
        </Button>
      )}
    </div>
  );
}
